import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import ThemeToggle from './ThemeToggle';
import JobModal from './JobModal';
import JobNotesModal from './JobNotesModal';
import JobCompletedDialog from './JobCompletedDialog';
import InvoiceModal from './InvoiceModal';
import { jobService } from '../services/jobService';
import { customerService } from '../services/customerService';
import { siteService } from '../services/siteService';
import { invoiceService } from '../services/invoiceService';

const SortIcon = ({ column, sortConfig }) => {
  const active = sortConfig.key === column;
  const asc = active && sortConfig.dir === 'asc';
  const desc = active && sortConfig.dir === 'desc';
  return (
    <span className="inline-flex flex-col ml-1 align-middle" style={{ lineHeight: 1 }}>
      <svg width="8" height="5" viewBox="0 0 8 5" className={asc ? 'text-blue-500' : 'text-gray-300 dark:text-gray-600'}><path d="M4 0L8 5H0z" fill="currentColor"/></svg>
      <svg width="8" height="5" viewBox="0 0 8 5" className={desc ? 'text-blue-500' : 'text-gray-300 dark:text-gray-600'} style={{marginTop:2}}><path d="M4 5L0 0H8z" fill="currentColor"/></svg>
    </span>
  );
};

const Jobs = () => {
  const [jobs, setJobs] = useState([]);
  const [customers, setCustomers] = useState(null);
  const [sites, setSites] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [notesJob, setNotesJob] = useState(null);
  const [completedJobDialog, setCompletedJobDialog] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', dir: 'desc' });
  const [activeTab, setActiveTab] = useState('All');
  const [generateInvoiceJob, setGenerateInvoiceJob] = useState(null);

  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const fetchJobs = useCallback(async () => {
    try {
      setLoading(true);
      const data = await jobService.getJobs();
      setJobs(data.data || []);
      setError('');
    } catch (err) {
      setError('Failed to load jobs. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchJobs();
    customerService.getCustomers().then(res => setCustomers(res.data || [])).catch(() => setCustomers([]));
    siteService.getSites().then(res => setSites(res.data || [])).catch(() => setSites([]));
  }, [fetchJobs]);

  const showSuccess = (message) => {
    setSuccessMessage(message);
    setTimeout(() => setSuccessMessage(''), 3000);
  };

  const handleAddJob = async (formData) => {
    await jobService.createJob(formData);
    await fetchJobs();
    setShowModal(false);
    showSuccess('Job added successfully!');
  };

  const handleEditJob = async (formData) => {
    const wasCompleted = editingJob.status === 'Completed';
    const becomingCompleted = formData.status === 'Completed';
    const vatRateChanged = String(formData.vat_rate_id) !== String(editingJob.vat_rate_id || '');
    const savedJob = editingJob;
    await jobService.updateJob(savedJob.id, formData);

    let invoiceUpdated = false;
    if (vatRateChanged && formData.vat_rate_id) {
      try {
        const invoicesRes = await invoiceService.getInvoices();
        const allInvoices = invoicesRes.data || [];
        invoiceUpdated = allInvoices.some(inv => String(inv.job_id) === String(savedJob.id));
      } catch {}
    }

    await fetchJobs();
    setEditingJob(null);
    setShowModal(false);
    if (!wasCompleted && becomingCompleted) {
      setCompletedJobDialog({ job: savedJob, formData });
    } else {
      showSuccess(invoiceUpdated
        ? 'Job updated. Invoice total updated to reflect new VAT rate.'
        : 'Job updated successfully!');
    }
  };

  const handleDeleteJob = async (id) => {
    try {
      await jobService.deleteJob(id);
      await fetchJobs();
      setDeleteConfirm(null);
      showSuccess('Job deleted successfully!');
    } catch (err) {
      setError('Failed to delete job. Please try again.');
    }
  };

  const handleGenerateInvoice = async (formData) => {
    await invoiceService.createInvoice(formData);
    setGenerateInvoiceJob(null);
    showSuccess('Invoice created successfully!');
    await fetchJobs();
  };

  const openEditModal = (job) => {
    setEditingJob(job);
    setShowModal(true);
  };

  const [noPrereqTarget, setNoPrereqTarget] = useState(null);

  const openAddModal = () => {
    if (customers !== null && customers.length === 0) {
      setNoPrereqTarget('customer');
      return;
    }
    if (sites !== null && sites.length === 0) {
      setNoPrereqTarget('site');
      return;
    }
    setEditingJob(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingJob(null);
  };

  const formatValue = (value) => {
    if (value === null || value === undefined || value === '') return '—';
    return `£${parseFloat(value).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const calcTotal = (value, vatRate) => {
    const net = parseFloat(value);
    const rate = vatRate ? parseFloat(vatRate.rate) : null;
    if (!rate || isNaN(net)) return null;
    return net * (1 + rate / 100);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    const d = new Date(dateStr);
    return d.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
      + ' ' + d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  };

  const handleSort = (key) => {
    setSortConfig(prev =>
      prev.key === key
        ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
        : { key, dir: 'asc' }
    );
  };

  const sortedJobs = [...jobs].sort((a, b) => {
    const { key, dir } = sortConfig;
    let aVal, bVal;
    if (key === 'created_at') {
      aVal = new Date(a.created_at).getTime();
      bVal = new Date(b.created_at).getTime();
    } else if (key === 'site') {
      aVal = (a.site?.name || '').toLowerCase();
      bVal = (b.site?.name || '').toLowerCase();
    } else if (key === 'value') {
      aVal = parseFloat(a.value) || 0;
      bVal = parseFloat(b.value) || 0;
    } else if (key === 'status') {
      aVal = (a.status || '').toLowerCase();
      bVal = (b.status || '').toLowerCase();
    } else {
      aVal = (a[key] || '').toString().toLowerCase();
      bVal = (b[key] || '').toString().toLowerCase();
    }
    if (aVal < bVal) return dir === 'asc' ? -1 : 1;
    if (aVal > bVal) return dir === 'asc' ? 1 : -1;
    return 0;
  });

  const JOB_STATUSES = ['Quoted', 'In Progress', 'Completed', 'Cancelled'];

  const displayedJobs = activeTab === 'All' ? sortedJobs : sortedJobs.filter(j => (j.status || 'Quoted') === activeTab);

  const thClass = 'text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700 dark:hover:text-gray-200 transition-colors';

  const statusBadge = (status) => {
    const styles = {
      'Quoted':      'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300',
      'In Progress': 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/40 dark:text-yellow-300',
      'Completed':   'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
      'Cancelled':   'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    };
    const cls = styles[status] || styles['Quoted'];
    return <span className={`inline-block px-2 py-0.5 rounded-full text-xs font-semibold ${cls}`}>{status || 'Quoted'}</span>;
  };
  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
        <div className="max-w-6xl mx-auto px-2.5 py-5">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
            <div className="flex items-center gap-1.5 text-sm">
              <Link to="/dashboard" className="text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors">Dashboard</Link>
              <span className="text-gray-400 dark:text-gray-600">/</span>
              <span className="text-gray-900 dark:text-white font-medium">Jobs</span>
            </div>
            <div className="flex flex-wrap items-center gap-3 sm:gap-4 w-full sm:w-auto">
              <ThemeToggle />
              <span className="text-gray-600 dark:text-gray-300 font-medium text-sm sm:text-base order-last sm:order-none">
                {user?.name}
              </span>
              <button
                onClick={logout}
                className="bg-red-500 text-white border-0 rounded-md px-3 sm:px-4 py-2 text-sm font-medium cursor-pointer transition-all duration-200 hover:bg-red-600"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-2.5 py-8">

        {successMessage && (
          <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4 text-green-700 dark:text-green-300 text-sm font-medium">
            ✓ {successMessage}
          </div>
        )}

        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {noPrereqTarget === 'customer' && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="text-3xl">⚠️</div>
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-1">No customers found</h3>
              <p className="text-yellow-700 dark:text-yellow-400 text-sm">
                You need at least one customer (and a site) before adding jobs. Create a customer first.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setNoPrereqTarget(null)} className="px-3 py-2 border border-yellow-400 text-yellow-700 dark:text-yellow-300 rounded-lg font-semibold text-sm hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors">Dismiss</button>
              <button onClick={() => navigate('/customers')} className="whitespace-nowrap px-4 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition-colors text-sm">Create Customer →</button>
            </div>
          </div>
        )}

        {noPrereqTarget === 'site' && (
          <div className="mb-6 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-300 dark:border-yellow-700 rounded-xl p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="text-3xl">⚠️</div>
            <div className="flex-1">
              <h3 className="font-semibold text-yellow-800 dark:text-yellow-300 mb-1">No sites found</h3>
              <p className="text-yellow-700 dark:text-yellow-400 text-sm">
                You need at least one site before adding jobs. Create a site first.
              </p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setNoPrereqTarget(null)} className="px-3 py-2 border border-yellow-400 text-yellow-700 dark:text-yellow-300 rounded-lg font-semibold text-sm hover:bg-yellow-100 dark:hover:bg-yellow-900/30 transition-colors">Dismiss</button>
              <button onClick={() => navigate('/sites')} className="whitespace-nowrap px-4 py-2.5 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg font-semibold transition-colors text-sm">Create Site →</button>
            </div>
          </div>
        )}

        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">Job List</h2>
            <p className="text-gray-500 dark:text-gray-400 text-sm mt-1">
              {activeTab === 'All'
                ? `${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'} total`
                : `${displayedJobs.length} of ${jobs.length} ${jobs.length === 1 ? 'job' : 'jobs'}`}
            </p>
          </div>
          <button
            onClick={openAddModal}
            className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity shadow-md"
          >
            <span className="text-lg leading-none">+</span>
            Add Job
          </button>
        </div>

        {/* Status summary */}
        {jobs.length > 0 && (() => {
          const STATUS_BORDER = {
            'Quoted':      'border-blue-300 dark:border-blue-700',
            'In Progress': 'border-yellow-300 dark:border-yellow-700',
            'Completed':   'border-green-300 dark:border-green-700',
            'Cancelled':   'border-gray-300 dark:border-gray-600',
          };
          const summaryStatuses = JOB_STATUSES.filter(s =>
            jobs.some(j => (j.status || 'Quoted') === s)
          );
          return (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
              {summaryStatuses.map(s => {
                const group = jobs.filter(j => (j.status || 'Quoted') === s);
                const total = group.reduce((sum, j) => {
                  const gross = calcTotal(j.value, j.vat_rate);
                  return sum + (gross !== null ? gross : parseFloat(j.value || 0));
                }, 0);
                return (
                  <button
                    key={s}
                    onClick={() => setActiveTab(s)}
                    className={`text-left p-3 bg-white dark:bg-gray-800 rounded-xl border-2 ${STATUS_BORDER[s] || STATUS_BORDER['Quoted']} shadow-sm hover:shadow-md transition-all ${activeTab === s ? 'ring-2 ring-blue-400 dark:ring-blue-500' : ''}`}
                  >
                    {statusBadge(s)}
                    <div className="mt-2 text-base font-bold text-gray-900 dark:text-white tabular-nums">{formatValue(total)}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{group.length} job{group.length !== 1 ? 's' : ''}</div>
                  </button>
                );
              })}
            </div>
          );
        })()}

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
          {/* Status Tabs */}
          <div className="flex overflow-x-auto border-b border-gray-200 dark:border-gray-700">
            {['All', ...JOB_STATUSES].map(tab => {
              const count = tab === 'All' ? jobs.length : jobs.filter(j => (j.status || 'Quoted') === tab).length;
              const active = activeTab === tab;
              return (
                <button
                  key={tab}
                  onClick={() => setActiveTab(tab)}
                  className={`px-4 py-3 text-sm font-medium border-b-2 whitespace-nowrap transition-colors -mb-px ${
                    active
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:border-gray-300 dark:hover:border-gray-600'
                  }`}
                >
                  {tab}
                  <span className={`ml-2 text-xs px-1.5 py-0.5 rounded-full ${
                    active ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'
                  }`}>{count}</span>
                </button>
              );
            })}
          </div>
          {loading ? (
            <div className="flex justify-center items-center py-20">
              <div className="flex items-center gap-3 text-gray-500 dark:text-gray-400">
                <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 border-t-blue-500 rounded-full animate-spin"></div>
                Loading jobs...
              </div>
            </div>
          ) : jobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="text-5xl mb-4">🔧</div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No jobs yet</h3>
              <p className="text-gray-500 dark:text-gray-400 mb-6">Add your first job to get started.</p>
              <button
                onClick={openAddModal}
                className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                <span className="text-lg leading-none">+</span>
                Add Job
              </button>
            </div>
          ) : displayedJobs.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-center">
              <div className="text-4xl mb-3">🔍</div>
              <p className="text-gray-500 dark:text-gray-400">No <strong>{activeTab}</strong> jobs.</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block overflow-x-auto">
                <table className="w-full whitespace-nowrap">
                  <thead>
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-700/50">
                      <th className={thClass} onClick={() => handleSort('created_at')}>Created<SortIcon column="created_at" sortConfig={sortConfig}/></th>
                      <th className={thClass} onClick={() => handleSort('title')}>Title<SortIcon column="title" sortConfig={sortConfig}/></th>
                      <th className={thClass} onClick={() => handleSort('site')}>Site<SortIcon column="site" sortConfig={sortConfig}/></th>
                      <th className="text-left px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Customer PO</th>
                      <th className={thClass} onClick={() => handleSort('value')}>Value<SortIcon column="value" sortConfig={sortConfig}/></th>
                      <th className={thClass} onClick={() => handleSort('status')}>Status<SortIcon column="status" sortConfig={sortConfig}/></th>
                      <th className="sticky right-0 text-right px-6 py-4 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]" style={{ backgroundColor: '#2b3544' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                    {displayedJobs.map((job) => (
                      <tr key={job.id} className="text-sm hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{formatDate(job.created_at)}</td>
                        <td className="px-6 py-4 font-medium">
                          <Link to={`/jobs/${job.id}`} className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline" onClick={e => e.stopPropagation()}>{job.title}</Link>
                        </td>
                        <td className="px-6 py-4">
                          {job.site
                            ? <Link to={`/sites/${job.site_id}`} className="text-gray-600 dark:text-white hover:underline font-medium" onClick={e => e.stopPropagation()}>{job.site.name}</Link>
                            : <span className="text-gray-400">—</span>}
                        </td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300">{job.customer_po || <span className="text-gray-400">—</span>}</td>
                        <td className="px-6 py-4 text-gray-600 dark:text-gray-300 font-medium">
                          <span>{formatValue(job.value)}</span>
                          {calcTotal(job.value, job.vat_rate) !== null && (
                            <span className="block text-xs text-gray-400 dark:text-gray-500 font-normal">
                              Total: {formatValue(calcTotal(job.value, job.vat_rate))} ({parseFloat(job.vat_rate.rate)}% VAT)
                            </span>
                          )}
                        </td>
                        <td className="px-6 py-4">{statusBadge(job.status)}</td>
                        <td className="sticky right-0 bg-white dark:bg-gray-800 px-6 py-4 text-right shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.05)] dark:shadow-[-4px_0_6px_-2px_rgba(0,0,0,0.2)]">
                          <div className="flex justify-end gap-2">
                            <button
                              onClick={() => setNotesJob(job)}
                              className="px-3 py-1.5 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-md transition-colors font-medium"
                            >
                              Notes{job.notes_count > 0 && (
                                <span className="ml-1.5 inline-flex items-center justify-center w-4 h-4 text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full font-bold">{job.notes_count}</span>
                              )}
                            </button>
                            <button
                              onClick={() => setGenerateInvoiceJob(job)}
                              className="px-3 py-1.5 text-sm text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-md transition-colors font-medium"
                            >
                              Invoice
                            </button>
                            <button
                              onClick={() => openEditModal(job)}
                              className="px-3 py-1.5 text-sm text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-md transition-colors font-medium"
                            >
                              Edit
                            </button>
                            <button
                              onClick={() => setDeleteConfirm(job)}
                              className="px-3 py-1.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors font-medium"
                            >
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-gray-200 dark:divide-gray-700">
                {displayedJobs.map((job) => (
                  <div key={job.id} className="p-5 space-y-1 hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors">
                    <div className="flex justify-between items-start">
                      <Link to={`/jobs/${job.id}`} className="font-semibold text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline">{job.title}</Link>
                      <div className="flex gap-2 ml-2">
                        <button
                          onClick={() => setNotesJob(job)}
                          className="text-sm text-gray-500 dark:text-gray-400 font-medium"
                        >
                          Notes{job.notes_count > 0 && (
                            <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400 rounded-full font-bold">{job.notes_count}</span>
                          )}
                        </button>
                        <button
                          onClick={() => setGenerateInvoiceJob(job)}
                          className="text-sm text-purple-600 dark:text-purple-400 font-medium"
                        >
                          Invoice
                        </button>
                        <button
                          onClick={() => openEditModal(job)}
                          className="text-sm text-blue-600 dark:text-blue-400 font-medium"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => setDeleteConfirm(job)}
                          className="text-sm text-red-600 dark:text-red-400 font-medium"
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                    {job.site && <Link to={`/sites/${job.site_id}`} className="text-sm font-medium text-gray-600 dark:text-white hover:underline">{job.site.name}</Link>}
                    {job.description && <p className="text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{job.description}</p>}
                    <div className="flex items-center justify-between pt-1">
                      <div>
                        <span className="text-sm font-semibold text-gray-900 dark:text-white">{formatValue(job.value)}</span>
                        {calcTotal(job.value, job.vat_rate) !== null && (
                          <span className="block text-xs text-gray-400 dark:text-gray-500">
                            Total: {formatValue(calcTotal(job.value, job.vat_rate))} ({parseFloat(job.vat_rate.rate)}% VAT)
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-400">{formatDate(job.created_at)}</span>
                    </div>
                    <div className="pt-1">{statusBadge(job.status)}</div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </main>

      {showModal && (
        <JobModal
          job={editingJob}
          onSave={editingJob ? handleEditJob : handleAddJob}
          onClose={closeModal}
        />
      )}

      {generateInvoiceJob && (
        <InvoiceModal
          defaultJobId={generateInvoiceJob.id}
          onSave={handleGenerateInvoice}
          onClose={() => setGenerateInvoiceJob(null)}
        />
      )}

      {notesJob && (
        <JobNotesModal
          job={notesJob}
          onClose={() => setNotesJob(null)}
        />
      )}

      {completedJobDialog && (
        <JobCompletedDialog
          job={completedJobDialog.job}
          formData={completedJobDialog.formData}
          onClose={(msg) => {
            setCompletedJobDialog(null);
            fetchJobs();
            if (msg) showSuccess(msg);
          }}
        />
      )}

      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl p-6 w-full max-w-sm">
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">Delete Job</h3>
            <p className="text-gray-600 dark:text-gray-300 mb-6">
              Are you sure you want to delete <strong>{deleteConfirm.title}</strong>? This cannot be undone.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 px-4 py-2.5 border-2 border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg font-semibold hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDeleteJob(deleteConfirm.id)}
                className="flex-1 px-4 py-2.5 bg-red-500 text-white rounded-lg font-semibold hover:bg-red-600 transition-colors"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Jobs;
